import { Component, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AuthService } from './core/auth/auth.service';
import { ThemeService } from './core/theme/theme.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, RouterLink, RouterLinkActive],
  templateUrl: './app.component.html'
})
export class AppComponent {
  private readonly auth = inject(AuthService);
  private readonly theme = inject(ThemeService);
  private readonly router = inject(Router);
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  protected readonly menuOpen = signal(false);
  protected readonly authenticated = computed(() => this.auth.isAuthenticated());
  protected readonly showNavigation = computed(() =>
    this.authenticated() && !isPublicAuthRoute(this.currentUrl())
  );
  protected readonly management = computed(() => this.auth.hasAnyRole(['SUPER_ADMIN', 'ORGANIZATION_ADMIN', 'COMPANY_ADMIN', 'QUALITY_MANAGER']));
  protected readonly superAdmin = computed(() => this.auth.hasAnyRole(['SUPER_ADMIN']));
  protected readonly organizationManagement = computed(() => this.auth.hasAnyRole(['SUPER_ADMIN', 'ORGANIZATION_ADMIN']));
  protected readonly courseEditor = computed(() => this.auth.hasAnyRole(['SUPER_ADMIN']));
  protected readonly driver = computed(() => this.auth.hasAnyRole(['DRIVER']));
  protected readonly title = computed(() => this.auth.currentUser()?.displayName ?? 'Willkommen');
  protected readonly currentSkin = this.theme.currentSkin;

  protected logout(): void {
    this.auth.logout();
  }

  protected toggleSkin(): void {
    this.theme.setSkin(this.currentSkin() === 'modern-premium' ? 'technical-precision' : 'modern-premium');
  }

  protected toggleMenu(): void {
    this.menuOpen.update((value) => !value);
  }

  protected closeMenu(): void {
    this.menuOpen.set(false);
  }
}

function isPublicAuthRoute(url: string): boolean {
  const path = url.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
  return path === '/login' || path === '/forgot-password' || path === '/reset-password' || path.startsWith('/verify/certificates/');
}
